import { isPlatformBrowser } from '@angular/common';
import { Directive, ElementRef, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { onlyDigits, stripCountryCode } from '../helpers/phone-br';

/**
 * Remove o DDI de valores colados ou preenchidos pelo autofill antes que o
 * ngx-mask processe o campo.
 *
 * O ngx-mask escuta o evento de input no próprio input. Para rodar antes dele
 * de forma determinística, o listener é registrado no elemento pai em fase de
 * captura — a captura em um ancestral sempre acontece antes dos listeners do
 * elemento alvo.
 *
 * Alterações de 1 dígito (digitação normal) passam intocadas, para que digitar
 * um dígito a mais em um número já completo não seja confundido com DDI.
 */
@Directive({
  selector: 'input[appPhoneBr]',
  standalone: true,
})
export class PhoneBrDirective implements OnInit, OnDestroy {
  private readonly host: HTMLInputElement;
  private container: HTMLElement | null = null;
  private lastDigits = '';

  constructor(
    el: ElementRef<HTMLInputElement>,
    @Inject(PLATFORM_ID) private readonly platformId: any,
  ) {
    this.host = el.nativeElement;
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.container = this.host.parentElement;
    if (!this.container) return;

    this.container.addEventListener('input', this.onInputCapture, true);
    this.host.addEventListener('focus', this.syncLastDigits);
    this.host.addEventListener('blur', this.syncLastDigits);
  }

  ngOnDestroy(): void {
    this.container?.removeEventListener('input', this.onInputCapture, true);
    this.host.removeEventListener('focus', this.syncLastDigits);
    this.host.removeEventListener('blur', this.syncLastDigits);
  }

  /** Mantém o baseline em dia mesmo quando o form é resetado por código. */
  private syncLastDigits = (): void => {
    this.lastDigits = onlyDigits(this.host.value);
  };

  private onInputCapture = (event: Event): void => {
    if (event.target !== this.host) return;

    const digits = onlyDigits(this.host.value);
    const isBulkChange = digits.length - this.lastDigits.length > 1;
    const normalized = isBulkChange ? stripCountryCode(digits) : digits;

    // o ngx-mask formata o valor logo na sequência, no listener dele
    if (normalized !== digits) this.host.value = normalized;

    this.lastDigits = normalized;
  };
}
